/* The Load Studio — the interactive load planner on the eight use-case pages.

   The prototype built this diagram with React.createElement from a simulation of
   the whole trip at fifteen-minute resolution. The DOM capture brought across a
   SNAPSHOT of one frame: the SVG rendered, its groups carried data-unit /
   data-src / data-load / data-days, and the copy underneath said "Everything in
   the frame is a control" — but no script existed, so nothing was.

   The render is LIFTED from the prototype rather than rewritten: mkEl below is
   createElement-compatible, so the 22KB of diagram code runs unchanged and the
   geometry cannot drift from the design. What is written here is only the part
   React used to provide — an element factory, a state object, five handlers and
   a re-render.

   studioSim() and stPin() are the prototype's own methods, adapted only where
   they reached for React's `this`.
*/
(function () {
  'use strict';

  var node = document.getElementById('ef-studio-data');
  if (!node) return;
  var DATA;
  try { DATA = JSON.parse(node.textContent); } catch (e) { return; }
  var STUDIOS = DATA.studios, EB_FOR = DATA.ebFor, PRODUCTS = DATA.products;
  var SCENES = DATA.scenes, STU_SLOTS = DATA.stuSlots,
      ST_ART_T = DATA.stArtT, ST_ICONS = DATA.stIcons;

  var byId = function (id) { return PRODUCTS.filter(function (x) { return x.id === id; })[0] || PRODUCTS[0]; };
  function money(n) { return 'RM ' + n.toLocaleString('en-MY'); }
  var stSrc = function (d) {
    return d.pid ? byId(d.pid)
      : { id: 'mains', short: d.name, out: d.out, price: 0, page: null, spec: '', mains: true };
  };

  /* createElement, but it builds real SVG DOM.

     React lowercases and hyphenates camelCased SVG attributes on output, with a
     handful that keep their capitals. KEEP is that handful — get it wrong and
     viewBox silently becomes viewbox, which SVG ignores, and the whole diagram
     collapses to its intrinsic size. */
  var SVG_NS = 'http://www.w3.org/2000/svg';
  var KEEP = { viewBox: 1, preserveAspectRatio: 1, patternUnits: 1, gradientUnits: 1,
               clipPathUnits: 1, markerWidth: 1, markerHeight: 1, refX: 1, refY: 1,
               baseFrequency: 1, gradientTransform: 1, spreadMethod: 1 };
  function attrName(k) {
    if (KEEP[k]) return k;
    if (k === 'className') return 'class';
    if (k === 'xlinkHref') return 'xlink:href';
    return k.replace(/[A-Z]/g, function (c) { return '-' + c.toLowerCase(); });
  }
  function mkEl(tag, props, children) {
    var el = document.createElementNS(SVG_NS, tag);
    if (props) {
      for (var k in props) {
        if (!Object.prototype.hasOwnProperty.call(props, k)) continue;
        var v = props[k];
        if (v == null || v === false) continue;
        /* key and onClick were React's; the click path is delegation below, so
           the handler is dropped rather than bound per node — there are several
           hundred of them on a full render. */
        if (k === 'key' || k.slice(0, 2) === 'on') continue;
        if (k === 'style' && typeof v === 'object') {
          for (var p in v) {
            if (!Object.prototype.hasOwnProperty.call(v, p)) continue;
            el.style.setProperty(p.replace(/[A-Z]/g, function (c) { return '-' + c.toLowerCase(); }), v[p]);
          }
          continue;
        }
        el.setAttribute(attrName(k), v);
      }
    }
    var kids = Array.prototype.slice.call(arguments, 2);
    if (kids.length === 1 && Array.isArray(kids[0])) kids = kids[0];
    kids.forEach(function add(c) {
      if (c == null || c === false) return;
      if (Array.isArray(c)) { c.forEach(add); return; }
      el.appendChild(c.nodeType ? c : document.createTextNode(String(c)));
    });
    return el;
  }

  /* ---------------------------------------------------------------- state */
  var mount = document.querySelector('[data-st-pin] [data-sc-svg]');
  var pinEl = document.querySelector('[data-st-pin]');
  var studioEl = document.querySelector('[data-st-studio]');
  if (!mount || !pinEl) return;

  /* Which use case this page is. The section carries it; falling back to the
     first config keeps a mis-tagged page rendering something rather than
     throwing on STUDIOS[undefined]. */
  var page = (studioEl && studioEl.getAttribute('data-st-page'))
             || Object.keys(STUDIOS)[0];
  if (!STUDIOS[page]) page = Object.keys(STUDIOS)[0];

  var s = null, stStop = null;
  function reset() {
    var r = (STUDIOS[page] || {}).reset || {};
    s = {
      page: page,
      stUnit: r.unit || (STUDIOS[page].units || [])[0],
      stEB: !!r.eb,
      stOff: Object.assign({}, r.off || {}),
      stAlt: r.alt || 0,
      stSun: r.sun || 0,
      stDays: r.days || 1,
      stT: s ? s.stT : 0
    };
  }
  reset();

/* A trip starts in the morning, not at midnight. Beginning at 00:00 opened the
   timeline in the middle of the first night — the reader met a battery already
   draining before anything had happened — and closed there too. From 07:00 the
   run opens just after first light (dawn 6.9) and, since every day is a whole
   24 hours, ends in daylight as well. */
var DAY_START = 7;
/* The verdict sentence is a long conditional inside buildDiagram. Rather than
   write it twice — two copies of a rule about what to advise is two rules that
   will diverge — the diagram stashes the one it just computed and the panel
   reads it back. */
var LAST_ADVICE = '';
function clockAt(elapsed) { return (DAY_START + elapsed) % 24; }
/* An elapsed-hours offset within a day, for a given clock hour. */
function offsetOf(hr) { return (hr - DAY_START + 24) % 24; }

/* Is a load running at clock hour h?
   A window may cross midnight, and until now it could not: the test was
   `h >= from && h < to`, so a fan set 21->6 was never on at any hour, and the
   only way to express "overnight" was to stop it at 24. Every such load — the
   tent fan, the heated blanket, the aircon you sleep under, the freezer that
   cannot be switched off — therefore went idle at midnight, and the studio
   drew 0W through the small hours, which is exactly when a camping battery is
   doing its work. from > to now means the window wraps. */
function loadOn(l, h) {
  return l.from <= l.to ? (h >= l.from && h < l.to)
                        : (h >= l.from || h < l.to);
}

function studioSim() {
  const cfg = STUDIOS[s.page] || STUDIOS.uc4x4;
  const unit = byId(s.stUnit) || byId(cfg.reset.unit);
  const panel = stSrc(cfg.srcB), alt = stSrc(cfg.srcA);
  // An extra battery only counts when it is one the chosen unit actually takes.
  const eb = s.stEB ? byId(EB_FOR[unit.id] || '') : null;
  const cap = unit.cap + (eb ? eb.cap : 0), step = 0.25, days = s.stDays, n = Math.round(days * 24 / step);
  const live = l => !s.stOff[l.id];
  const plateAt = h => cfg.loads.reduce((a, l) => a + (live(l) && loadOn(l, h) ? l.w : 0), 0);
  const drawAt = h => cfg.loads.reduce((a, l) => a + (live(l) && loadOn(l, h) ? (l.dc ? l.w : l.w / 0.9) : 0), 0);
  const chargeAt = h => {
    let w = 0;
    if (s.stAlt > 0 && h >= cfg.driveFrom && h < cfg.driveFrom + s.stAlt) w += alt.out;
    if (s.stSun > 0 && h >= cfg.sunFrom && h < cfg.sunFrom + s.stSun) w += Math.round(panel.out * 0.62);
    return w;
  };
  let wh = cap, min = cap, empty = null, gross = 0, cin = 0;
  const series = [];
  for (let i = 0; i <= n; i++) {
    const t = i * step, h = clockAt(t);
    series.push(wh / cap);
    if (i === n) break;
    const d = wh > 0 ? drawAt(h) : 0, c = chargeAt(h);
    if (t < 24) { gross += drawAt(h) * step; cin += c * step; }
    wh = Math.max(0, Math.min(cap, wh + (c - d) * step));
    if (wh <= 0.5 && empty === null) empty = t + step;
    if (wh < min) min = wh;
  }
  return {
    unit, panel, alt, eb, cap, step, days, n, series, empty,
    minPct: Math.round(min / cap * 100), endPct: Math.round(series[series.length - 1] * 100),
    dayWh: Math.round(gross), inWh: Math.round(cin),
    plateAt, drawAt, chargeAt
  };
}

  /* -------------------------------------------------------------- diagram */
  function buildDiagram() {
    var isPhone = matchMedia('(max-width: 767px)').matches;
  const cfg = STUDIOS[s.page], sim = studioSim(), h = mkEl;
      const clock = t => {
        const hh = Math.floor(t % 24), mm = Math.round((t % 1) * 60);
        return (hh < 10 ? '0' : '') + hh + ':' + (mm < 10 ? '0' : '') + mm;
      };
      const idx = Math.min(Math.round(s.stT * sim.n), sim.series.length - 1), tAbs = idx * sim.step, hNow = clockAt(tAbs);
      const dayNo = Math.min(sim.days, Math.floor(tAbs / 24) + 1);
      const socNow = sim.series[Math.min(idx, sim.series.length - 1)];
      const plate = sim.plateAt(hNow), chg = sim.chargeAt(hNow);
      const isNight = hNow >= cfg.dusk || hNow < cfg.dawn;
      // timeline geometry
      const X0 = 46, X1 = 1180, Y0 = 26, Y1 = 152;
      const xOf = i => X0 + (X1 - X0) * i / (sim.series.length - 1);
      const yOf = v => Y1 - (Y1 - Y0) * Math.max(0, Math.min(1, v));
      const curve = sim.series.map((v, i) => (i ? 'L' : 'M') + xOf(i).toFixed(1) + ' ' + yOf(v).toFixed(1)).join(' ');
      const area = curve + ' L' + xOf(sim.series.length - 1).toFixed(1) + ' ' + Y1 + ' L' + X0 + ' ' + Y1 + ' Z';
      const bandsN = [], bandsC = [], ticks = [];
      for (let d = 0; d < sim.days; d++) {
        /* ox takes an ELAPSED offset into the day; hx takes a CLOCK hour and
           converts. Keeping the two apart is what makes a 07:00 start work —
           the night no longer straddles the day boundary, so it is one band. */
        const ox = off => xOf((d * 24 + off) / sim.step);
        const hx = hr => ox(offsetOf(hr));
        const od = offsetOf(cfg.dusk), oa = offsetOf(cfg.dawn);
        if (od < oa) {
          bandsN.push({ key: 'n' + d, x: ox(od), w: Math.max(0, ox(oa) - ox(od)) });
        } else {
          bandsN.push({ key: 'n' + d, x: ox(od), w: Math.max(0, ox(24) - ox(od)) });
          bandsN.push({ key: 'm' + d, x: ox(0), w: Math.max(0, ox(oa) - ox(0)) });
        }
        if (s.stAlt > 0) bandsC.push({ key: 'a' + d, x: hx(cfg.driveFrom), w: ox(offsetOf(cfg.driveFrom) + s.stAlt) - hx(cfg.driveFrom), kind: 'Alternator' });
        if (s.stSun > 0) bandsC.push({ key: 's' + d, x: hx(cfg.sunFrom), w: ox(offsetOf(cfg.sunFrom) + s.stSun) - hx(cfg.sunFrom), kind: 'Solar' });
        for (let off = 0; off < 24; off += (sim.days > 3 ? 12 : 6)) {
          const c = Math.round(clockAt(off));
          ticks.push({ key: 't' + d + '-' + off, x: ox(off), label: (c < 10 ? '0' : '') + c + ':00' });
        }
      }
      // the diagram
      const prim = (a, i) => a.t === 'l' ? h('line', { key: 'a' + i, x1: a.x1, y1: a.y1, x2: a.x2, y2: a.y2 })
        : a.t === 'c' ? h('circle', { key: 'a' + i, cx: a.cx, cy: a.cy, r: a.r })
        : a.t === 'r' ? h('rect', { key: 'a' + i, x: a.x, y: a.y, width: a.w, height: a.h, rx: a.rx || 0 })
        : h('path', { key: 'a' + i, d: a.d });
      const ink = 'var(--text)';
      const scene = SCENES[s.page] || {};
      const sc3d = scene.art3d || null, scFlat = scene.art || null;
      const AMB = isNight ? 'var(--ef-amber-400)' : 'var(--ef-amber-600)';
      const SKY = isNight ? 'var(--ef-blue-300)' : 'var(--ef-blue-600)';
      const EFB = 'var(--ef-blue-500)';
      const nodeEls = cfg.loads.map((l, i) => {
        const sl = STU_SLOTS[i], off = !!s.stOff[l.id], act = !off && loadOn(l, hNow);
        const edge = 'M840 385 H' + (858 + i * 8) + ' V' + (sl.y + 37) + ' H' + sl.x;
        // The diagram is a control, not a picture: click a box to drop that load.
        return h('g', { key: 'n' + i, 'data-load': l.id, onClick: onStLoad, style: { cursor: 'pointer' }, opacity: off ? 0.2 : (act ? 1 : 0.42) }, [
          h('path', { key: 'e', d: edge, fill: 'none', stroke: act ? 'var(--ef-blue-500)' : 'var(--border-strong)', strokeWidth: act ? 2 : 1.2, strokeDasharray: '7 9', style: act ? { animation: 'efFlow 1.1s linear infinite' } : null }),
          h('rect', { key: 'b', x: sl.x, y: sl.y, width: 310, height: 74, fill: act ? 'rgba(63,104,224,0.16)' : 'var(--bg-subtle)', stroke: act ? 'var(--ef-blue-500)' : 'var(--border-strong)', strokeWidth: act ? 1.8 : 1.2 }),
          h('text', { key: 't', x: sl.x + 18, y: sl.y + 30, fontSize: 16, fill: ink, textDecoration: off ? 'line-through' : 'none' }, l.k),
          h('text', { key: 'w', x: sl.x + 18, y: sl.y + 58, fontSize: 19, fontWeight: 500, fill: act ? SKY : 'var(--text-muted)', style: { fontVariantNumeric: 'tabular-nums' } }, off ? 'switched off' : (act ? l.w.toLocaleString('en-MY') + 'W' : 'idle')),
          h('text', { key: 'p', x: sl.x + 292, y: sl.y + 58, textAnchor: 'end', fontSize: 11, letterSpacing: '0.12em', fill: 'var(--text-muted)' }, l.dc ? 'DC' : 'AC'),
          // An EcoFlow load is also something we sell: this corner is a link
          // out to its page, so it must not toggle the load on the way past.
          l.pid ? h('g', { key: 'lk', 'data-page': byId(l.pid).page, style: { cursor: 'pointer' } }, [
            h('rect', { key: 'h', x: sl.x + 262, y: sl.y + 6, width: 42, height: 26, fill: 'var(--bg-subtle)', stroke: 'var(--border-strong)', strokeWidth: 1 }),
            h('text', { key: 'a', x: sl.x + 283, y: sl.y + 24, textAnchor: 'middle', fontSize: 13, fill: 'var(--text)' }, 'View')
          ]) : null
        ]);
      });
      const socH = Math.max(3, Math.round(240 * socNow));
      // Charge sources left, the battery centre, the loads right. Everything
      // carrying a data- attribute is a control: the graph IS the panel.
      const pillW = 152, pillX0 = 380;
      const unitPills = cfg.units.map((id, i) => {
        const p = byId(id), sel = id === s.stUnit, x = pillX0 + i * (pillW + 8);
        return h('g', { key: 'u' + i, 'data-unit': id, onClick: onStUnit, style: { cursor: 'pointer' } }, [
          h('rect', { key: 'b', x, y: 34, width: pillW, height: 62, fill: sel ? 'rgba(63,104,224,0.22)' : 'var(--bg-subtle)', stroke: sel ? EFB : 'var(--border-strong)', strokeWidth: sel ? 1.8 : 1.2 }),
          h('text', { key: 'n', x: x + 14, y: 60, fontSize: 14.5, fontWeight: 500, fill: sel ? 'var(--text)' : 'var(--text-secondary)' }, p.short),
          h('text', { key: 'c', x: x + 14, y: 82, fontSize: 12, fill: 'var(--text-muted)', style: { fontVariantNumeric: 'tabular-nums' } }, p.capText)
        ]);
      });
      const altLive = s.stAlt > 0 && hNow >= cfg.driveFrom && hNow < cfg.driveFrom + s.stAlt;
      const sunLive = s.stSun > 0 && hNow >= cfg.sunFrom && hNow < cfg.sunFrom + s.stSun;
      const srcEls = [
        { id: 'alt', y: 196, tgt: 300, col: AMB, name: sim.alt.short, hrs: s.stAlt, txt: s.stAlt === 1 ? '1 hour ' + cfg.srcA.verb : s.stAlt + ' hours ' + cfg.srcA.verb, wh: s.stAlt ? Math.round(sim.alt.out * s.stAlt * 0.9) : 0, live: altLive, icon: ST_ICONS[cfg.srcA.icon] },
        { id: 'sun', y: 380, tgt: 480, col: SKY, name: sim.panel.short, hrs: s.stSun, txt: s.stSun === 1 ? '1 hour ' + cfg.srcB.verb : s.stSun + ' hours ' + cfg.srcB.verb, wh: s.stSun ? Math.round(sim.panel.out * 0.62 * s.stSun) : 0, live: sunLive, icon: ST_ICONS[cfg.srcB.icon] }
      ].map((c, i) => h('g', { key: 'sr' + i, 'data-src': c.id, onClick: onStSrc, style: { cursor: 'pointer' }, opacity: c.hrs > 0 ? 1 : 0.44 }, [
        h('path', { key: 'f', d: 'M340 ' + (c.y + 70) + ' H' + (438 + i * 16) + ' V' + c.tgt + ' H560', fill: 'none', stroke: c.live ? c.col : 'var(--border-strong)', strokeWidth: c.live ? 2.4 : 1.2, strokeDasharray: '7 9', style: c.live ? { animation: 'efFlow 1.1s linear infinite' } : null }),
        h('rect', { key: 'b', x: 60, y: c.y, width: 280, height: 140, fill: c.hrs > 0 ? 'var(--bg-subtle)' : 'var(--bg-sunken)', stroke: c.hrs > 0 ? c.col : 'var(--border-strong)', strokeWidth: c.hrs > 0 ? 1.8 : 1.2 }),
        h('circle', { key: 'ic', cx: 112, cy: c.y + 70, r: 30, fill: 'none', stroke: c.hrs > 0 ? c.col : 'var(--border-strong)', strokeWidth: 1.4 }),
        h('path', { key: 'ig', d: c.icon, transform: 'translate(112,' + (c.y + 70) + ')', fill: 'none', stroke: c.hrs > 0 ? c.col : 'var(--border-strong)', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' }),
        h('text', { key: 't', x: 158, y: c.y + 48, fontSize: 16, fill: 'var(--text)' }, c.name),
        h('text', { key: 'h', x: 158, y: c.y + 78, fontSize: 14, fill: 'var(--text-muted)' }, c.hrs > 0 ? c.txt : 'not used \u2014 tap to add'),
        h('text', { key: 'w', x: 158, y: c.y + 112, fontSize: 19, fontWeight: 500, fill: c.hrs > 0 ? c.col : 'var(--text-muted)', style: { fontVariantNumeric: 'tabular-nums' } }, '+' + c.wh.toLocaleString('en-MY') + 'Wh')
      ]));
      const dayPills = [1, 2, 3, 4, 5, 6, 7].map((d, i) => {
        const sel = d === s.stDays, x = 60 + i * 62;
        return h('g', { key: 'dp' + i, 'data-days': d, onClick: onStDays, style: { cursor: 'pointer' } }, [
          h('rect', { key: 'b', x, y: 610, width: 56, height: 56, fill: sel ? 'rgba(63,104,224,0.22)' : 'var(--bg-subtle)', stroke: sel ? EFB : 'var(--border-strong)', strokeWidth: sel ? 1.8 : 1.2 }),
          h('text', { key: 't', x: x + 28, y: 645, textAnchor: 'middle', fontSize: 16, fill: sel ? 'var(--text)' : 'var(--text-secondary)', style: { fontVariantNumeric: 'tabular-nums' } }, d)
        ]);
      });
      // The extra battery, offered only on a unit that takes one.
      const ebProd = byId(EB_FOR[s.stUnit] || '');
      const ebAvail = !!ebProd;
      const ebPill = ebAvail ? h('g', { key: 'eb', onClick: onStEB, style: { cursor: 'pointer' } }, [
        h('rect', { key: 'b', x: 560, y: 592, width: 280, height: 58, fill: s.stEB ? 'rgba(63,104,224,0.22)' : 'var(--bg-subtle)', stroke: s.stEB ? EFB : 'var(--border-strong)', strokeWidth: s.stEB ? 1.8 : 1.2, strokeDasharray: s.stEB ? null : '5 5' }),
        h('text', { key: 't', x: 578, y: 617, fontSize: 14.5, fontWeight: 500, fill: s.stEB ? 'var(--text)' : 'var(--text-secondary)' }, (s.stEB ? '\u2212 ' : '+ ') + 'Extra battery'),
        h('text', { key: 'c', x: 578, y: 638, fontSize: 12, fill: 'var(--text-muted)', style: { fontVariantNumeric: 'tabular-nums' } }, s.stEB ? 'Fitted \u2014 ' + sim.cap.toLocaleString('en-MY') + 'Wh total' : ebProd.capText + ' \u2014 tap to fit')
      ]) : null;
      const cap = (x, y, t) => h('text', { key: 'c' + x + y, x, y, fontSize: 11, letterSpacing: '0.14em', fill: 'var(--text-muted)' }, t);
      const diagram = h('svg', {
        viewBox: '0 0 1400 875', preserveAspectRatio: 'xMidYMid meet', role: 'img', 'data-sc-svg': '',
        'aria-label': 'Live load diagram at ' + clock(hNow) + '. ' + plate + ' watts drawing, ' + Math.round(socNow * 100) + ' per cent charge. Charge sources on the left, the battery in the middle, the loads on the right.',
        style: { width: '100%', height: '100%', display: 'block' }
      }, [
        h('g', { key: 'art', transform: sc3d ? 'translate(512,411) scale(0.8)' : ST_ART_T, opacity: sc3d ? (isNight ? 1 : 0.5) : (isNight ? 0.5 : 0.34), fill: sc3d ? null : 'none', stroke: sc3d ? null : 'var(--border-strong)', strokeWidth: sc3d ? null : 1.4, strokeLinejoin: 'round', strokeLinecap: 'round' },
          sc3d
            ? sc3d.filter(a => !(a.n && !isNight) && !(a.d0 && isNight)).map((a, i) => a.t === 'e'
              ? h('ellipse', { key: 'b' + i, cx: a.cx, cy: a.cy, rx: a.rx, ry: a.ry, fill: a.f, opacity: a.o == null ? 1 : a.o })
              : h('path', { key: 'b' + i, d: a.d, fill: a.f || 'none', opacity: a.o == null ? 1 : a.o, stroke: a.s || 'none', strokeWidth: a.sw || 0, strokeLinecap: 'round', strokeLinejoin: 'round' }))
            : (scFlat || []).map(prim)),
        cap(60, 178, 'CHARGING IN \u2014 TAP TO CHANGE'),
        cap(pillX0, 24, 'THE BATTERY \u2014 TAP TO SWAP'),
        cap(1040, 36, 'RUNNING NOW \u2014 TAP TO SWITCH OFF'),
        cap(60, 596, cfg.daysCap),
        // The clock lives inside the frame as well as above it: while the act is
        // pinned, the frame is the only thing on screen, so the time and the
        // runtime have to travel with it.
        h('g', { key: 'clk' }, [
          h('text', { key: 't', x: 60, y: 84, fontSize: 46, fontWeight: 500, fill: 'var(--text)', style: { fontVariantNumeric: 'tabular-nums' } }, clock(hNow)),
          h('text', { key: 'd', x: 60, y: 112, fontSize: 14, fill: 'var(--text-secondary)' }, sim.days > 1 ? 'Day ' + dayNo + ' of ' + sim.days : (isNight ? 'After dark' : 'Daylight')),
          h('text', { key: 'n', x: 60, y: 134, fontSize: 14, fill: 'var(--text-muted)' }, sim.days > 1 ? (isNight ? 'After dark' : 'Daylight') : ''),
          h('text', { key: 'w', x: 60, y: 158, fontSize: 15, fill: 'var(--text)', style: { fontVariantNumeric: 'tabular-nums' } }, 'Drawing ' + plate.toLocaleString('en-MY') + 'W'),
          h('text', { key: 'c', x: 200, y: 158, fontSize: 15, fill: chg ? SKY : 'var(--text-muted)', style: { fontVariantNumeric: 'tabular-nums' } }, chg ? '+' + chg.toLocaleString('en-MY') + 'W in' : '')
        ]),
        h('g', { key: 'src' }, srcEls),
        h('g', { key: 'up' }, unitPills),
        h('g', { key: 'dp' }, dayPills),
        ebPill,
        h('g', { key: 'bat' }, [
          h('rect', { key: 'b', x: 560, y: 200, width: 280, height: 370, fill: 'rgba(63,104,224,0.16)', stroke: EFB, strokeWidth: 2 }),
          h('text', { key: 'n', x: 580, y: 240, fontSize: 17, fill: 'var(--text)' }, sim.unit.short + (sim.eb ? ' + EB' : '')),
          h('rect', { key: 'bb', x: 580, y: 264, width: 44, height: 240, fill: 'none', stroke: 'var(--border-strong)' }),
          h('rect', { key: 'bf', x: 580, y: 264 + (240 - socH), width: 44, height: socH, fill: socNow < 0.2 ? 'var(--ef-red-500)' : EFB }),
          h('text', { key: 'p', x: 648, y: 400, fontSize: 60, fontWeight: 500, fill: 'var(--text)', style: { fontVariantNumeric: 'tabular-nums' } }, Math.round(socNow * 100) + '%'),
          h('text', { key: 'c', x: 648, y: 432, fontSize: 12, letterSpacing: '0.1em', fill: chg ? SKY : 'var(--text-muted)' }, chg ? '+' + chg.toLocaleString('en-MY') + 'W IN' : 'ON BATTERY'),
          h('text', { key: 'd', x: 580, y: 548, fontSize: 12, letterSpacing: '0.1em', fill: 'var(--text-muted)' }, 'DRAWING ' + plate.toLocaleString('en-MY') + 'W')
        ]),
        h('g', { key: 'nd' }, nodeEls)
      ]);

      // ---- phone composition -------------------------------------------
      // The landscape graph cannot be legible and unscrolled at 375px, so the
      // phone gets its own stacked frame. Its height budget is set so the
      // WIDTH binds under preserveAspectRatio="meet" — if height binds, every
      // label shrinks and the whole point is lost.
      const mCap = (x, y, t) => h('text', { key: 'mc' + x + y, x, y, fontSize: 12.5, letterSpacing: '0.12em', fill: 'var(--text-muted)' }, t);
      const mSrc = [
        { id: 'alt', name: sim.alt.short, hrs: s.stAlt, col: AMB, wh: s.stAlt ? Math.round(sim.alt.out * s.stAlt * 0.9) : 0, verb: cfg.srcA.verb },
        { id: 'sun', name: sim.panel.short, hrs: s.stSun, col: SKY, wh: s.stSun ? Math.round(sim.panel.out * 0.62 * s.stSun) : 0, verb: cfg.srcB.verb }
      ].map((c, i) => h('g', { key: 'ms' + i, 'data-src': c.id, onClick: onStSrc, style: { cursor: 'pointer' }, opacity: c.hrs > 0 ? 1 : 0.5 }, [
        h('rect', { key: 'b', x: 20, y: 118 + i * 62, width: 440, height: 54, fill: c.hrs > 0 ? 'var(--bg-subtle)' : 'var(--bg-sunken)', stroke: c.hrs > 0 ? c.col : 'var(--border-strong)', strokeWidth: c.hrs > 0 ? 1.6 : 1.1 }),
        h('text', { key: 'n', x: 38, y: 141 + i * 62, fontSize: 16, fill: 'var(--text)' }, c.name),
        h('text', { key: 'h', x: 38, y: 161 + i * 62, fontSize: 14, fill: 'var(--text-muted)' }, c.hrs > 0 ? (c.hrs === 1 ? '1 hour ' + c.verb : c.hrs + ' hours ' + c.verb) : 'not used \u2014 tap to add'),
        h('text', { key: 'w', x: 442, y: 152 + i * 62, textAnchor: 'end', fontSize: 17, fontWeight: 500, fill: c.hrs > 0 ? c.col : 'var(--text-muted)', style: { fontVariantNumeric: 'tabular-nums' } }, '+' + c.wh.toLocaleString('en-MY') + 'Wh')
      ]));
      const mUnits = cfg.units.map((id, i) => {
        const p = byId(id), sel = id === s.stUnit, pitch = 440 / cfg.units.length, x = 20 + i * pitch;
        return h('g', { key: 'mu' + i, 'data-unit': id, onClick: onStUnit, style: { cursor: 'pointer' } }, [
          h('rect', { key: 'b', x, y: 274, width: pitch - 6, height: 50, fill: sel ? 'rgba(63,104,224,0.22)' : 'var(--bg-subtle)', stroke: sel ? EFB : 'var(--border-strong)', strokeWidth: sel ? 1.6 : 1.1 }),
          h('text', { key: 'n', x: x + 12, y: 296, fontSize: 14.5, fontWeight: 500, fill: sel ? 'var(--text)' : 'var(--text-secondary)' }, p.short),
          h('text', { key: 'c', x: x + 12, y: 315, fontSize: 13, fill: 'var(--text-muted)', style: { fontVariantNumeric: 'tabular-nums' } }, p.capText)
        ]);
      });
      const mEB = ebAvail ? h('g', { key: 'meb', onClick: onStEB, style: { cursor: 'pointer' } }, [
        h('rect', { key: 'b', x: 20, y: 336, width: 440, height: 46, fill: s.stEB ? 'rgba(63,104,224,0.22)' : 'var(--bg-subtle)', stroke: s.stEB ? EFB : 'var(--border-strong)', strokeWidth: s.stEB ? 1.6 : 1.1, strokeDasharray: s.stEB ? null : '5 5' }),
        h('text', { key: 't', x: 38, y: 358, fontSize: 14, fontWeight: 500, fill: s.stEB ? 'var(--text)' : 'var(--text-secondary)' }, (s.stEB ? '\u2212 ' : '+ ') + 'Extra battery'),
        h('text', { key: 'c', x: 38, y: 376, fontSize: 13, fill: 'var(--text-muted)', style: { fontVariantNumeric: 'tabular-nums' } }, s.stEB ? 'Fitted \u2014 ' + sim.cap.toLocaleString('en-MY') + 'Wh total' : ebProd.capText + ' \u2014 tap to fit')
      ]) : null;
      const mBatY = ebAvail ? 396 : 336;
      const mSocW = Math.max(4, Math.round(400 * socNow));
      const mCols = cfg.loads.length > 5 ? 2 : 1;
      const mColW = mCols === 2 ? 216 : 440;
      const mLoadY0 = mBatY + 80;
      const mLoads = cfg.loads.map((l, i) => {
        const off = !!s.stOff[l.id];
        const x = 20 + (i % mCols) * (mColW + 8);
        const y = mLoadY0 + Math.floor(i / mCols) * 44;
        return h('g', { key: 'ml' + i, 'data-load': l.id, onClick: onStLoad, style: { cursor: 'pointer' }, opacity: off ? 0.42 : 1 }, [
          h('rect', { key: 'b', x, y, width: mColW, height: 38, fill: off ? 'var(--bg-sunken)' : 'rgba(63,104,224,0.14)', stroke: off ? 'var(--border-strong)' : 'var(--ef-blue-500)', strokeWidth: off ? 1.1 : 1.5 }),
          h('text', { key: 't', x: x + 13, y: y + 24, fontSize: 14.5, fill: 'var(--text)', textDecoration: off ? 'line-through' : 'none' }, l.k),
          h('text', { key: 'w', x: x + mColW - 13, y: y + 24, textAnchor: 'end', fontSize: 14.5, fontWeight: 500, fill: off ? 'var(--text-muted)' : SKY, style: { fontVariantNumeric: 'tabular-nums' } }, off ? 'off' : l.w.toLocaleString('en-MY') + 'W')
        ]);
      });
      const mLoadEnd = mLoadY0 + Math.ceil(cfg.loads.length / mCols) * 44;
      const mDayY = mLoadEnd + 30;
      const mDays = [1, 2, 3, 4, 5, 6, 7].map((d, i) => {
        const sel = d === s.stDays, x = 20 + i * 63;
        return h('g', { key: 'md' + i, 'data-days': d, onClick: onStDays, style: { cursor: 'pointer' } }, [
          h('rect', { key: 'b', x, y: mDayY, width: 57, height: 46, fill: sel ? 'rgba(63,104,224,0.22)' : 'var(--bg-subtle)', stroke: sel ? EFB : 'var(--border-strong)', strokeWidth: sel ? 1.6 : 1.1 }),
          h('text', { key: 't', x: x + 28.5, y: mDayY + 29, textAnchor: 'middle', fontSize: 16, fill: sel ? 'var(--text)' : 'var(--text-secondary)', style: { fontVariantNumeric: 'tabular-nums' } }, d)
        ]);
      });
      const phoneDiagram = h('svg', {
        viewBox: '0 0 480 ' + (mDayY + 64), preserveAspectRatio: 'xMidYMid meet', role: 'img', 'data-sc-svg': '',
        'aria-label': 'Load studio at ' + clock(hNow) + '. ' + plate + ' watts drawing, ' + Math.round(socNow * 100) + ' per cent charge.',
        style: { width: '100%', height: 'auto', display: 'block' }
      }, [
        h('g', { key: 'mclk' }, [
          h('text', { key: 't', x: 20, y: 48, fontSize: 40, fontWeight: 500, fill: 'var(--text)', style: { fontVariantNumeric: 'tabular-nums' } }, clock(hNow)),
          h('text', { key: 'd', x: 20, y: 72, fontSize: 14, fill: 'var(--text-secondary)' }, (sim.days > 1 ? 'Day ' + dayNo + ' of ' + sim.days + ' \u00b7 ' : '') + (isNight ? 'After dark' : 'Daylight')),
          h('text', { key: 'w', x: 460, y: 44, textAnchor: 'end', fontSize: 17, fontWeight: 500, fill: 'var(--text)', style: { fontVariantNumeric: 'tabular-nums' } }, plate.toLocaleString('en-MY') + 'W out'),
          h('text', { key: 'c', x: 460, y: 68, textAnchor: 'end', fontSize: 14, fill: chg ? SKY : 'var(--text-muted)', style: { fontVariantNumeric: 'tabular-nums' } }, chg ? '+' + chg.toLocaleString('en-MY') + 'W in' : 'on battery')
        ]),
        mCap(20, 104, 'CHARGING IN \u2014 TAP TO CHANGE'),
        h('g', { key: 'msrc' }, mSrc),
        mCap(20, 262, 'THE BATTERY \u2014 TAP TO SWAP'),
        h('g', { key: 'mup' }, mUnits),
        mEB,
        h('g', { key: 'mbat' }, [
          h('rect', { key: 'f', x: 20, y: mBatY, width: 440, height: 44, fill: 'none', stroke: EFB, strokeWidth: 1.6 }),
          h('rect', { key: 'b', x: 40, y: mBatY + 13, width: 400, height: 18, fill: 'none', stroke: 'var(--border-strong)' }),
          h('rect', { key: 'v', x: 40, y: mBatY + 13, width: mSocW, height: 18, fill: socNow < 0.2 ? 'var(--ef-red-500)' : EFB }),
          h('text', { key: 'p', x: 20, y: mBatY - 8, fontSize: 15, fontWeight: 500, fill: 'var(--text)', style: { fontVariantNumeric: 'tabular-nums' } }, Math.round(socNow * 100) + '% \u00b7 ' + sim.unit.short + (sim.eb ? ' + EB' : ''))
        ]),
        mCap(20, mBatY + 68, 'RUNNING NOW \u2014 TAP TO SWITCH OFF'),
        h('g', { key: 'mnd' }, mLoads),
        mCap(20, mDayY - 12, cfg.daysCap),
        h('g', { key: 'mdp' }, mDays)
      ]);
      const advice = sim.empty === null
        ? 'Holds all ' + sim.days + (sim.days > 1 ? ' nights' : ' night') + ' with ' + sim.endPct + '% left.'
        : (s.stSun === 0 ? 'Add the ' + sim.panel.short + ': four hours of sun is about ' + Math.round(sim.panel.out * 0.62 * 4) + 'Wh back into the pack.'
          : s.stAlt === 0 ? (sim.alt.mains
              ? 'Two hours on mains is about ' + Math.round(sim.alt.out * 2 * 0.9) + 'Wh back into the pack.'
              : 'Two hours ' + cfg.srcA.verb + ' is about ' + Math.round(sim.alt.out * 2 * 0.9) + 'Wh back through the ' + sim.alt.short + '.')
          : 'Go up a size' + (ebAvail && !s.stEB ? ', add the extra battery,' : ',') + ' or drop the ' + (function () {
              const big = cfg.loads.filter(l => !s.stOff[l.id]).sort((a, b) => b.w - a.w).slice(0, 2);
              return big.length ? big.map(l => l.k.toLowerCase()).join(' and the ') : 'heaviest loads';
            })() + '.');
      LAST_ADVICE = advice;   /* after the statement, not inside it */
    
    return isPhone ? phoneDiagram : diagram;
  }

  /* ---------------------------------------------- state-of-charge chart */
  /* The chart below the studio shipped as static SVG: it drew one fixed trip
     while the studio above it simulated another, so changing the battery, the
     days or the loads moved the diagram and left the curve untouched. It uses
     the same geometry the diagram's own timeline does (X 46..1180, Y 26..152),
     so one series paints both. */
  function paintChart(sim) {
    var svg = document.querySelector('[data-st-chart]');
    if (!svg) return;
    var cfg = STUDIOS[s.page] || STUDIOS.uc4x4;
    sim = sim || studioSim();
    var X0 = 46, X1 = 1180, Y0 = 26, Y1 = 152, last = sim.series.length - 1;
    var xOf = function (i) { return X0 + (X1 - X0) * i / last; };
    var yOf = function (v) { return Y1 - (Y1 - Y0) * Math.max(0, Math.min(1, v)); };
    var curve = sim.series.map(function (v, i) {
      return (i ? 'L' : 'M') + xOf(i).toFixed(1) + ' ' + yOf(v).toFixed(1);
    }).join(' ');

    var line = svg.querySelector('[data-st-chart-line]');
    var fill = svg.querySelector('[data-st-chart-fill]');
    if (line) line.setAttribute('d', curve);
    if (fill) fill.setAttribute('d', curve + ' L' + xOf(last).toFixed(1) + ' '
                                 + Y1 + ' L' + X0 + ' ' + Y1 + ' Z');

    /* Bands and ticks are rebuilt, not repositioned: their COUNT moves with the
       number of days, so there is no stable set to reposition. */
    var anchor = fill || line;
    if (!anchor || !anchor.parentNode) return;
    var host = anchor.parentNode;
    var drop = function (sel) {
      Array.prototype.slice.call(svg.querySelectorAll(sel))
        .forEach(function (n) { n.parentNode.removeChild(n); });
    };
    drop('[data-st-chart-band]');
    drop('[data-st-chart-tick]');

    var NS = 'http://www.w3.org/2000/svg';
    function band(x, w, kind) {
      if (!(w > 0)) return;
      var r = document.createElementNS(NS, 'rect');
      r.setAttribute('data-st-chart-band', kind);
      r.setAttribute('x', x.toFixed(1));
      r.setAttribute('y', Y0);
      r.setAttribute('width', w.toFixed(1));
      r.setAttribute('height', Y1 - Y0);
      r.setAttribute('fill', kind === 'charge'
        ? 'rgba(63,104,224,0.28)' : 'var(--bg-subtle)');
      host.insertBefore(r, anchor);
    }
    var tickEvery = sim.days > 3 ? 12 : 6;
    for (var d = 0; d < sim.days; d++) {
      /* Same split as the diagram's timeline: ox takes an elapsed offset into
         the day, hx converts a clock hour into one. The chart and the diagram
         are two views of one trip, so they must share the 07:00 start. */
      var ox = (function (dd) {
        return function (off) { return xOf((dd * 24 + off) / sim.step); };
      })(d);
      var hx = function (hr) { return ox(offsetOf(hr)); };
      var od = offsetOf(cfg.dusk), oa = offsetOf(cfg.dawn);
      if (od < oa) {
        band(ox(od), Math.max(0, ox(oa) - ox(od)), 'dark');
      } else {
        band(ox(od), Math.max(0, ox(24) - ox(od)), 'dark');
        band(ox(0), Math.max(0, ox(oa) - ox(0)), 'dark');
      }
      if (s.stAlt > 0) band(hx(cfg.driveFrom), ox(offsetOf(cfg.driveFrom) + s.stAlt) - hx(cfg.driveFrom), 'charge');
      if (s.stSun > 0) band(hx(cfg.sunFrom), ox(offsetOf(cfg.sunFrom) + s.stSun) - hx(cfg.sunFrom), 'charge');
      for (var off = 0; off < 24; off += tickEvery) {
        var c = Math.round(clockAt(off));
        var t = document.createElementNS(NS, 'text');
        t.setAttribute('data-st-chart-tick', '');
        t.setAttribute('x', ox(off).toFixed(1));
        t.setAttribute('y', 178);
        t.setAttribute('text-anchor', 'middle');
        t.setAttribute('font-size', 11);
        t.setAttribute('fill', 'var(--text-muted)');
        t.setAttribute('font-family', 'var(--ef-font-mono)');
        t.textContent = (c < 10 ? '0' : '') + c + ':00';
        svg.appendChild(t);
      }
    }
  }

  /* The frame goes dark after dark. The token layer re-declares colour on
     [data-theme], so a nested island needs nothing but the attribute — and the
     studio is exactly that: a lit panel inside a light page. */
  function paintNight(sim) {
    var cfg = STUDIOS[s.page] || STUDIOS.uc4x4;
    sim = sim || studioSim();
    var idx = Math.min(Math.round(s.stT * sim.n), sim.series.length - 1);
    var hNow = clockAt(idx * sim.step);   /* 07:00 start, same as everywhere else */
    var night = hNow >= cfg.dusk || hNow < cfg.dawn;
    var frame = (mount && mount.closest && mount.closest('[data-st-pin]')) || null;
    if (frame) {
      frame.setAttribute('data-theme', night ? 'dark' : 'light');
      frame.setAttribute('data-st-night', night ? 'on' : 'off');
    }
  }

  /* The verdict panel. Same fault the chart had: it states a headline and four
     figures the simulation already computes, but shipped as literals — so
     swapping the battery moved the diagram and left a verdict about the trip
     the reader had just changed sitting underneath it. */
  function paintVerdict(sim) {
    if (!sim) return;
    var g = function (k) { return document.querySelector('[data-st-verdict="' + k + '"]'); };
    var wh = function (v) { return Math.round(v).toLocaleString('en-MY') + 'Wh'; };
    var line = g('line');
    if (line && LAST_ADVICE) line.textContent = LAST_ADVICE;
    var used = g('used'); if (used) used.textContent = wh(sim.dayWh);
    var back = g('back'); if (back) back.textContent = wh(sim.inWh);
    var low = g('lowest'); if (low) low.textContent = sim.minPct + '%';
    var out = g('empty');
    if (out) {
      if (sim.empty === null) {
        out.textContent = 'Never empties';
      } else {
        var day = Math.floor(sim.empty / 24) + 1;
        var c = clockAt(sim.empty);
        var hh = Math.floor(c), mm = Math.round((c - hh) * 60);
        out.textContent = 'Day ' + day + ', ' + (hh < 10 ? '0' : '') + hh
                        + ':' + (mm < 10 ? '0' : '') + mm;
      }
    }
  }

  function render() {
    var next;
    try { next = buildDiagram(); }
    catch (e) { return; }   /* leave the last good frame up rather than a blank box */
    /* replaceWith, not replaceChildren: `mount` IS the <svg>, and putting the
       new <svg> inside it nests one in the other — the outer element keeps the
       captured snapshot's viewBox and aria-label while the real diagram renders
       invisibly within, which reads exactly like "nothing happened". */
    mount.replaceWith(next);
    mount = next;
    bindControls();
    /* Both are views of the same simulation, so they update together or the
       page contradicts itself. Neither may take the render down with it. */
    /* One simulation for both views, rather than each re-running it. */
    var sim = null;
    try { sim = studioSim(); } catch (e) {}
    try { paintChart(sim); } catch (e) {}
    try { paintNight(sim); } catch (e) {}
    try { paintVerdict(sim); } catch (e) {}
  }

  /* Rebound after every render, since replaceWith discards the old subtree. One
     listener on the current <svg> rather than per control. */
  function bindControls() {
    mount.addEventListener('click', onPick);
  }
  function onPick(e) {
    var g = e.target.closest && e.target.closest('[data-unit],[data-src],[data-load],[data-days],[data-eb]');
    if (!g) return;
    if (g.hasAttribute('data-unit')) return onStUnit(g.getAttribute('data-unit'));
    if (g.hasAttribute('data-src')) return onStSrc(g.getAttribute('data-src'));
    if (g.hasAttribute('data-load')) return onStLoad(g.getAttribute('data-load'));
    if (g.hasAttribute('data-days')) return onStDays(+g.getAttribute('data-days'));
    if (g.hasAttribute('data-eb')) return onStEB();
  }

  /* ------------------------------------------------------------- controls */
  /* One delegated listener rather than a handler per node: a full render is
     several hundred elements and they are all replaced on every change. */
  function onStUnit(id) { s.stUnit = id; render(); }
  function onStEB() { s.stEB = !s.stEB; render(); }
  function onStLoad(id) { s.stOff[id] = s.stOff[id] ? 0 : 1; render(); }
  function onStDays(n) { s.stDays = n; render(); }
  function onStSrc(which) {
    var cfg = STUDIOS[s.page];
    var d = which === 'alt' ? cfg.srcA : cfg.srcB;
    var key = which === 'alt' ? 'stAlt' : 'stSun';
    var step = d.step || 1, max = d.max || 4;
    s[key] = s[key] + step > max ? 0 : s[key] + step;
    render();
  }


  var resetBtn = studioEl && studioEl.querySelector('[data-st-reset]');
  if (resetBtn) resetBtn.addEventListener('click', function () { reset(); render(); });

function stPin() {
  if (stStop) { stStop(); stStop = null; }
  const pin = document.querySelector('[data-st-pin]');
  if (!pin || matchMedia('(max-width: 767px)').matches) return;
  // No pending-frame flag here. A rAF guard deadlocks if the frame scheduled
  // by the first call never fires (throttled or hidden iframe): the flag stays
  // set and every later scroll returns early, freezing the clock for good.
  // Quantising to 1/600 keeps the render count sane instead.
  const onScroll = () => {
    const span = pin.offsetHeight - innerHeight;
    const p = span > 0 ? Math.max(0, Math.min(1, -pin.getBoundingClientRect().top / span)) : 0;
    const q = Math.round(p * 600) / 600;
    if (Math.abs(q - s.stT) < 0.0016) return;
    s.stT = q; render();
  };
  addEventListener('scroll', onScroll, { passive: true });
  addEventListener('resize', onScroll, { passive: true });
  onScroll();
  stStop = () => {
    removeEventListener('scroll', onScroll);
    removeEventListener('resize', onScroll);
  };
}

  /* isPhone is read when the diagram is built, so a window that crosses 768px
     needs a rebuild — otherwise the desktop layout stays on a phone-width
     screen until something else happens to trigger a render. */
  var wasPhone = matchMedia('(max-width: 767px)').matches;
  addEventListener('resize', function () {
    var now = matchMedia('(max-width: 767px)').matches;
    if (now !== wasPhone) { wasPhone = now; render(); }
  }, { passive: true });

  render();
  stPin();
  addEventListener('pagehide', function () { if (stStop) stStop(); });
})();
